import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  TextField,
  Typography,
  Grid,
  Paper,
  Divider,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import { calculateMortgageSummary } from './mortgageUtils';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(value);
const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString('en-GB') : '');

const MortgageCalculatorApp = () => {
  const [propertyPrice, setPropertyPrice] = useState(300000);
  const [depositMode, setDepositMode] = useState('percent'); // 'percent' | 'amount'
  const [depositInput, setDepositInput] = useState(10); // percent or amount depending on mode
  const [interestRate, setInterestRate] = useState(3.5); // Annual interest rate
  const [termYears, setTermYears] = useState(25);

  const handleDepositModeChange = (nextMode) => {
    setDepositMode(nextMode);
    if (nextMode === 'amount') {
      setDepositInput((Number.isFinite(propertyPrice) ? propertyPrice : 0) * 0.1);
    } else {
      setDepositInput(depositPercent || 10);
    }
  };

  const parseNumberInput = (value) => {
    const sanitized = typeof value === 'string' ? value.replace(/,/g, '') : value;
    const parsed = parseFloat(sanitized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const {
    depositAmount,
    depositPercent,
    principal,
    monthlyPayment,
    totalPayment,
    totalInterest,
  } = calculateMortgageSummary({
    propertyPrice,
    depositMode,
    depositInput,
    interestRate,
    termYears,
  });

  return (
    <>
      <Helmet>
        <title>Mortgage Calculator | Talk About Finance</title>
        <meta
          name="description"
          content="Estimate monthly mortgage payments, total repayment, and interest across different loan terms and rates."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/mortgage-calculator" />
      </Helmet>

      <Container sx={{ mt: 6, mb: 8, pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => theme.shadows[1],
          background: (theme) =>
            theme.palette.mode === 'light'
              ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
              : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
        }}
        >
        <Stack spacing={1.5} sx={{ flex: 1, minWidth: 280, width: '100%' }}>
          <Box>
            <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
              Back to apps
            </Button>
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Calculator
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Mortgage Calculator
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '100%' }}>
            Estimate monthly payments, total repayment, and interest over the life of a loan. Adjust the inputs to see how rate and term affect your mortgage.
          </Typography>
        </Stack>
      </Paper>

      <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Property Price"
              variant="outlined"
              fullWidth
              type="text"
              value={formatNumber(propertyPrice)}
              onChange={(e) => setPropertyPrice(parseNumberInput(e.target.value))}
              InputProps={{
                startAdornment: <InputAdornment position="start">£</InputAdornment>,
                inputMode: 'decimal',
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Interest Rate (%)"
              variant="outlined"
              fullWidth
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value))}
              InputProps={{ inputProps: { step: 0.1, min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Term (Years)"
              variant="outlined"
              fullWidth
              type="number"
              value={termYears}
              onChange={(e) => setTermYears(parseInt(e.target.value))}
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Deposit
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="deposit-mode-label">Deposit Type</InputLabel>
              <Select
                labelId="deposit-mode-label"
                label="Deposit Type"
                value={depositMode}
                onChange={(e) => handleDepositModeChange(e.target.value)}
              >
                <MenuItem value="percent">Percentage of property price</MenuItem>
                <MenuItem value="amount">Manual deposit amount</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label={depositMode === 'percent' ? 'Deposit (%)' : 'Deposit Amount'}
              variant="outlined"
              fullWidth
              type="text"
              value={
                depositMode === 'percent' ? depositInput : formatNumber(depositInput)
              }
              onChange={(e) =>
                setDepositInput(
                  depositMode === 'percent'
                    ? parseNumberInput(e.target.value)
                    : parseNumberInput(e.target.value)
                )
              }
              InputProps={{
                inputProps: { min: 0, ...(depositMode === 'percent' ? { max: 100, step: 0.5 } : {}) },
                startAdornment:
                  depositMode === 'amount' ? (
                    <InputAdornment position="start">£</InputAdornment>
                  ) : undefined,
              }}
              helperText={
                depositMode === 'percent'
                  ? 'Percentage of the property price'
                  : 'Enter how much cash you will put down'
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ height: '100%', display: 'flex' }}>
              <Paper
                variant="outlined"
                sx={{ p: 2, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
              >
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Deposit amount</Typography>
                  <Typography variant="h6">{formatCurrency(depositAmount)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {depositPercent.toFixed(1)}% of property price
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Results
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography color="text.secondary">Loan amount</Typography>
          <Typography fontWeight="bold">{formatCurrency(principal)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography color="text.secondary">Monthly Payment</Typography>
          <Typography fontWeight="bold">{formatCurrency(monthlyPayment)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography color="text.secondary">Total Repayment</Typography>
          <Typography>{formatCurrency(totalPayment)}</Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography color="text.secondary">Total Interest</Typography>
          <Typography>{formatCurrency(totalInterest)}</Typography>
        </Box>
      </Paper>
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          About these calculations
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These figures are illustrative and based on the assumptions shown. They may not reflect the terms of specific financial products or your individual circumstances. You should verify results independently and, where appropriate, seek advice from a qualified professional.
        </Typography>
      </Box>
      </Container>
    </>
  );
};

export default MortgageCalculatorApp;
